import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '../store/auth.ts';

export const api: AxiosInstance = axios.create({ baseURL: '/api', withCredentials: true });

api.interceptors.request.use((cfg) => {
  const t = useAuthStore.getState().accessToken;
  if (t && cfg.headers) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// Recovery classification for a 401'd request:
//  - 'recovered': a fresh session was obtained (refresh or ambient SSO) → retry.
//  - 'dead':      the refresh token is genuinely revoked/expired (refresh 401
//                 and SSO bootstrap failed) → clear state and send to /login.
//  - 'transient': a network blip / 5xx / no-response error — the session is NOT
//                 known-bad, so do NOT log out; just reject and let the caller retry.
type Recovery = 'recovered' | 'dead' | 'transient';

async function recoverSession(): Promise<Recovery> {
  try {
    await useAuthStore.getState().refresh();
    return 'recovered';
  } catch (e) {
    const status = (e as { response?: { status?: number } })?.response?.status;
    if (status === 401) {
      // Refresh token genuinely revoked/expired — try the ambient wap_sso cookie
      // before giving up. ssoBootstrap swallows its own errors and returns bool.
      const ok = await useAuthStore.getState().ssoBootstrap(true);
      return ok ? 'recovered' : 'dead';
    }
    // No response (network) or a 5xx/other → transient. Never log out on these.
    return 'transient';
  }
}

// Single shared in-flight recovery so concurrent 401s don't stampede refresh.
let refreshing: Promise<Recovery> | null = null;
api.interceptors.response.use(
  (r) => r,
  async (err: { response?: { status?: number }; config?: InternalAxiosRequestConfig & { _retry?: boolean } }) => {
    const status = err.response?.status;
    const original = err.config;
    if (status === 401 && original && !original._retry) {
      original._retry = true;
      refreshing ??= recoverSession().finally(() => { refreshing = null; });
      const outcome = await refreshing;
      if (outcome === 'recovered') return api(original);
      if (outcome === 'dead') {
        useAuthStore.setState({ accessToken: null, refreshToken: null, user: null });
        if (typeof window !== 'undefined') window.location.href = '/login';
      }
      // 'transient' → fall through and reject; the session stays signed in.
    }
    throw err;
  },
);
